import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { LogOut, Package, Heart, User as UserIcon } from "lucide-react";
import { api, gbp, formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function Account() {
  const { user, login, register, logout, authReady, wishlist } = useStore();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "orders";
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);

  useEffect(() => {
    if (user) api.get("/my-orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (wishlist.length) Promise.all(wishlist.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))).then((res) => setWishProducts(res.filter(Boolean)));
    else setWishProducts([]);
  }, [wishlist]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.name, f.email, f.password);
      toast.success("Welcome to Crochet Kerry!");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!authReady) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Seo title="Account" />
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-luxe text-brand-pink mb-2">My account</p>
        <h1 className="font-serif-display text-4xl">{mode === "login" ? "Welcome back" : "Create an account"}</h1>
      </div>
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2.5 text-sm bg-white outline-none focus:border-brand-pink" data-testid="register-name" />}
        <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2.5 text-sm bg-white outline-none focus:border-brand-pink" data-testid="auth-email" />
        <input required type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full border border-brand-border rounded-sm px-3 py-2.5 text-sm bg-white outline-none focus:border-brand-pink" data-testid="auth-password" />
        <button className="w-full bg-brand-ink text-white py-3 rounded-sm text-sm uppercase tracking-luxe hover:bg-brand-pink hover:text-brand-ink transition-colors" data-testid="auth-submit">{mode === "login" ? "Sign in" : "Create account"}</button>
      </form>
      <p className="text-center text-sm text-muted-foreground mt-5">
        {mode === "login" ? "New here? " : "Already have an account? "}
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="underline text-brand-ink" data-testid="toggle-auth">{mode === "login" ? "Create an account" : "Sign in"}</button>
      </p>
    </div>
  );

  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="My Account" />
      <div className="flex items-center justify-between mb-8">
        <div><p className="text-xs uppercase tracking-luxe text-brand-pink mb-1">My account</p><h1 className="font-serif-display text-4xl">Hi, {user.name}</h1></div>
        {user.role === "admin" && <Link to="/admin" className="text-xs uppercase tracking-luxe underline">Admin dashboard</Link>}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="space-y-1">
          {[["orders", "Orders", Package], ["wishlist", "Wishlist", Heart], ["details", "Details", UserIcon]].map(([t, label, Icon]) => (
            <button key={t} onClick={() => setTab(t)} className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-sm text-sm ${tab === t ? "bg-brand-blush text-brand-ink" : "text-muted-foreground hover:bg-brand-blush/50"}`} data-testid={`account-tab-${t}`}><Icon className="w-4 h-4" /> {label}</button>
          ))}
          <button onClick={() => { logout(); navigate("/"); }} className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-sm text-sm text-muted-foreground hover:bg-brand-blush/50" data-testid="logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </aside>
        <div>
          {tab === "orders" && (
            <div data-testid="account-orders">
              <h2 className="font-serif-display text-2xl mb-5">Your orders</h2>
              {orders.length === 0 ? <p className="text-muted-foreground text-sm">No orders yet. <Link to="/shop" className="underline">Start shopping</Link>.</p> : (
                <div className="space-y-4">
                  {orders.map((o) => (
                    <div key={o.id} className="border border-brand-border rounded-sm p-5">
                      <div className="flex justify-between flex-wrap gap-2 mb-3">
                        <div><p className="font-medium">{o.order_number}</p><p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-GB")}</p></div>
                        <span className="text-xs uppercase tracking-luxe bg-brand-blush px-3 py-1 rounded-sm h-fit">{o.status}</span>
                      </div>
                      {o.items.map((it, i) => <p key={i} className="text-sm text-muted-foreground">{it.title} × {it.qty}</p>)}
                      <p className="font-medium mt-2">{gbp(o.total)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === "wishlist" && (
            <div data-testid="account-wishlist">
              <h2 className="font-serif-display text-2xl mb-5">Your wishlist</h2>
              {wishProducts.length === 0 ? <p className="text-muted-foreground text-sm">Your wishlist is empty. Tap the heart on any product to save it.</p> : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">{wishProducts.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
              )}
            </div>
          )}
          {tab === "details" && (
            <div data-testid="account-details">
              <h2 className="font-serif-display text-2xl mb-5">Your details</h2>
              <div className="space-y-2 text-sm"><p><span className="text-muted-foreground">Name:</span> {user.name}</p><p><span className="text-muted-foreground">Email:</span> {user.email}</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
